/* eslint-disable max-lines */
import { AxiosError, AxiosResponse } from 'axios';
import { JSX, useEffect, useRef, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  GetOrderHistoryAPI,
  getPurchaseHistoryFilters,
  qbCountAPI,
} from '../api/oms/order-history';
import { GetBigReceiptAPI } from '../api/oms/receipt';
import { ERR_GENERIC } from '../constants/response-error-messages';
import { IAlert } from '../models/common';
import {
  IOrderHistoryFilterResponse,
  IOrderHistoryModalHooks,
  IOrderHistoryResponse,
  IPHFilter,
  IPHFilterRequest,
  IPHRequest,
} from '../models/purchase-history';
import { pushErrorEvent } from '../utils/analytics';
import { createTimeStamp } from '../utils/date-utils';
import { openFile, parseBlob } from '../utils/http-helper';
import {
  setNoRecordsFoundError,
  setPurchaseHistoryPageError,
} from '../utils/purchase-history/analytics';
import { getSvocId, getUserId } from '../utils/user';
import { useExperienceContext } from './useContexts';
import {
  CPS_ORDER_HISTORY_CACHE_MAX_ENTRIES,
  CPS_ORDER_HISTORY_CACHE_TTL_MS,
  CpsOrderHistoryCache,
} from '../utils/purchase-history/cps-order-history-cache';
import {
  CPS_FILTERS_CACHE_MAX_ENTRIES,
  CPS_FILTERS_CACHE_TTL_MS,
  CpsOrderHistoryFilterCache,
} from '../utils/purchase-history/cps-order-history-filter-cache';

export const OrderHistoryPoJobNameHooks = () => {
  const [poJobNameState, setPoJobNameState] = useState('');

  return { poJobNameState, setPoJobNameState };
};

const cpsOrderHistoryCache = new CpsOrderHistoryCache(
  CPS_ORDER_HISTORY_CACHE_MAX_ENTRIES,
  CPS_ORDER_HISTORY_CACHE_TTL_MS,
);

export const OrderHistoryModalHooks = (): IOrderHistoryModalHooks => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [orderHistoryModalComponent, setOrderHistoryModalComponent] =
    useState<JSX.Element | null>(null);

  return {
    openModal,
    setOpenModal,
    orderHistoryModalComponent,
    setOrderHistoryModalComponent,
  };
};

export const GetOrderHistoryData = (
  requestData: IPHRequest,
  enabled = true,
) => {
  const { channel } = useExperienceContext();
  const { userID } = useThdCustomer() || {};
  const previousUserIdRef = useRef<string | undefined>(userID);
  const [orderHistoryData, setOrderHistoryData] = useState<
    IOrderHistoryResponse | undefined
  >(undefined);
  const [orderHistoryError, setOrderHistoryError] = useState<
    AxiosError | undefined
  >(undefined);
  const [orderHistoryLoaded, setOrderHistoryLoaded] = useState(false);
  const [cpsDataSource, setCPSDataSource] = useState<string | undefined>(
    undefined,
  );

  const wrappedRequest = { orderHistoryRequest: requestData };

  useEffect(() => {
    if (previousUserIdRef.current && previousUserIdRef.current !== userID) {
      cpsOrderHistoryCache.clear();
    }

    previousUserIdRef.current = userID;
  }, [userID]);

  useEffect(() => {
    const hasSearchValue = Boolean(requestData.searchValue?.trim());
    const normalizedFilters = (requestData.filters ?? [])
      .map((filter): IPHFilter => {
        const name = filter.name?.trim() ?? '';
        if ('values' in filter) {
          return {
            name,
            values: (filter.values ?? [])
              .map((val) => val.trim())
              .filter((val) => val.length > 0)
              .sort(),
          };
        }
        return {
          name,
          minValue: filter.minValue.trim(),
          maxValue: filter.maxValue.trim(),
        };
      })
      .filter((filter) => {
        if (!filter.name) return false;
        return 'values' in filter
          ? filter.values.length > 0
          : filter.minValue.length > 0 && filter.maxValue.length > 0;
      })
      .sort((filterA, filterB) => filterA.name.localeCompare(filterB.name));
    const hasAppliedFilters = normalizedFilters.length > 0;
    const shouldUseCache = hasSearchValue || hasAppliedFilters;
    const cacheKey = JSON.stringify({
      userID,
      channel,
      startDate: requestData.startDate,
      endDate: requestData.endDate,
      pageNumber: requestData.pageNumber,
      pageSize: requestData.pageSize,
      resultsFilter: requestData.resultsFilter,
      sortBy: requestData.sortBy,
      sortOrder: requestData.sortOrder,
      searchBy: requestData.searchBy,
      searchValue: requestData.searchValue?.trim(),
      filters: normalizedFilters,
    });

    if (shouldUseCache) {
      const cachedEntry = cpsOrderHistoryCache.get(cacheKey);
      const isCacheValid =
        Boolean(cachedEntry) &&
        Date.now() - (cachedEntry?.createdAt ?? 0) <
          CPS_ORDER_HISTORY_CACHE_TTL_MS;

      if (isCacheValid && cachedEntry) {
        setOrderHistoryData(cachedEntry.data);
        setOrderHistoryError(undefined);
        setCPSDataSource(cachedEntry.cpsDataSource);
        setOrderHistoryLoaded(true);
        return () => {
          setOrderHistoryError(undefined);
          setOrderHistoryLoaded(false);
        };
      }

      if (cachedEntry && !isCacheValid) {
        cpsOrderHistoryCache.delete(cacheKey);
      }
    }

    if (!enabled) return undefined;

    setOrderHistoryLoaded(false);
    GetOrderHistoryAPI(wrappedRequest, channel, userID)
      .then((response: AxiosResponse<IOrderHistoryResponse>) => {
        if (
          response.data.orderCount === 0 &&
          (hasSearchValue || hasAppliedFilters)
        )
          setNoRecordsFoundError();
        setOrderHistoryData(response.data);
        setCPSDataSource(response.headers.datasource);
        if (shouldUseCache) {
          cpsOrderHistoryCache.set(cacheKey, {
            data: response.data,
            cpsDataSource: response.headers.datasource,
            createdAt: Date.now(),
          });
        }
      })
      .catch((error: AxiosError) => {
        setPurchaseHistoryPageError(error);
        setOrderHistoryError(error);
        setCPSDataSource(error?.response?.headers?.datasource);
      })
      .finally(() => {
        setOrderHistoryLoaded(true);
      });

    return () => {
      setOrderHistoryError(undefined);
      setOrderHistoryLoaded(false);
    };
  }, [
    userID,
    channel,
    requestData.startDate,
    requestData.endDate,
    requestData.pageNumber,
    requestData.sortBy,
    requestData.sortOrder,
    requestData.resultsFilter,
    requestData.searchBy,
    requestData.searchValue,
    requestData.filters,
    enabled,
  ]);

  return {
    orderHistoryData,
    orderHistoryError,
    orderHistoryLoaded,
    cpsDataSource,
  };
};

export const GetCPSFilters = (
  requestData: IPHFilterRequest,
  enabled = true,
) => {
  const { channel } = useExperienceContext();
  const { userID } = useThdCustomer() || {};
  const cpsFiltersCacheRef = useRef<CpsOrderHistoryFilterCache>(
    new CpsOrderHistoryFilterCache(
      CPS_FILTERS_CACHE_MAX_ENTRIES,
      CPS_FILTERS_CACHE_TTL_MS,
    ),
  );
  const previousCustomerAccountIdRef = useRef<string | undefined>(
    requestData.customerAccountId,
  );
  const [getCPSFiltersResponse, setGetCPSFiltersResponse] = useState<
    IOrderHistoryFilterResponse | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [cpsFilterError, setCPSFilterError] = useState(false);

  const wrappedRequest = { orderHistoryFilterRequest: requestData };

  useEffect(() => {
    if (
      previousCustomerAccountIdRef.current &&
      previousCustomerAccountIdRef.current !== requestData.customerAccountId
    ) {
      cpsFiltersCacheRef.current.clear();
    }

    previousCustomerAccountIdRef.current = requestData.customerAccountId;
  }, [requestData.customerAccountId]);

  useEffect(() => {
    let isMounted = true;
    const normalizedFilterTypes = [...(requestData.filterTypes ?? [])]
      .map((filterType) => String(filterType).trim())
      .sort((filterTypeA, filterTypeB) =>
        filterTypeA.localeCompare(filterTypeB),
      );
    const cacheKey = JSON.stringify({
      channel,
      userID,
      requestData: {
        ...requestData,
        filterTypes: normalizedFilterTypes,
      },
    });

    if (!enabled) {
      setIsLoading(false);
      setGetCPSFiltersResponse(undefined);
      setCPSFilterError(false);
      return () => {
        isMounted = false;
      };
    }

    const cachedResponse = cpsFiltersCacheRef.current.get(cacheKey);
    if (cachedResponse) {
      setGetCPSFiltersResponse(cachedResponse.data);
      setCPSFilterError(false);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchCPSFilters = async () => {
      setIsLoading(true);
      try {
        const response: AxiosResponse<IOrderHistoryFilterResponse> =
          await getPurchaseHistoryFilters(wrappedRequest, channel, userID);
        if (isMounted) {
          cpsFiltersCacheRef.current.set(cacheKey, {
            data: response.data,
            createdAt: Date.now(),
          });
          setGetCPSFiltersResponse(response.data);
          setCPSFilterError(false);
        }
      } catch (error) {
        if (isMounted) {
          setCPSFilterError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCPSFilters();

    return () => {
      isMounted = false;
    };
  }, [
    requestData.startDate,
    requestData.endDate,
    requestData.filterTypes,
    requestData.customerAccountId,
    channel,
    userID,
    enabled,
  ]);

  return { getCPSFiltersResponse, isLoading, cpsFilterError };
};

export const GetBigReceiptHook = (
  encryptedBigReceiptRequest: string,
  setAlertState: (value: IAlert | null) => void,
) => {
  GetBigReceiptAPI({
    userId: getUserId() || '',
    customerAccountId: getSvocId() || '',
    encryptedBigReceiptRequest,
  })
    .then((response: AxiosResponse) => {
      setAlertState({
        alertTitle: 'Success',
        alertMessage: `Big Receipt has been successfully exported.
      Please check your browser downloads folder for the file.`,
        status: 'success',
      });

      const blob = parseBlob(response.data, 'data:application/pdf,');
      const fileName = `Order_Invoice${createTimeStamp()}.pdf`;
      openFile(blob, fileName);
    })
    .catch((error: AxiosError) => {
      const statusCode = error.response?.status.toString() || '';
      const statusMessage = error.response?.statusText || '';
      pushErrorEvent('purchase history', statusCode, statusMessage, 'inline');
      setAlertState({
        alertTitle: 'Failed',
        alertMessage: ERR_GENERIC.message,
        status: 'error',
      });
    });
};

/**
 * QuickBooks Hooks
 */
export const GetQBOrderCount = (
  request: IPHRequest,
  openModal?: boolean,
): number | undefined => {
  const { userID } = useThdCustomer() || {};
  const [qbOrderCount, setQBOrderCount] = useState<number | undefined>();
  const wrappedRequest = { orderHistoryRequest: request };
  useEffect(() => {
    if (openModal) {
      qbCountAPI(wrappedRequest, userID)
        .then((response) => {
          setQBOrderCount(response.data.orderCount);
        })
        .catch(() => {
          setQBOrderCount(undefined);
        });
    }
  }, [openModal, request.startDate, request.endDate]);

  return qbOrderCount;
};
